import { ref, uploadBytes, deleteObject } from "firebase/storage";
import { collection, query, where, getDocs, doc, deleteDoc, getFirestore } from "firebase/firestore";
import { db as firebaseDb } from '../../firebase';

/**
 * Image to Text Extraction Utility
 * 
 * This module handles the complete image-to-text workflow:
 * 1. Upload image to Firebase Storage
 * 2. Wait for Google Vision API to process and save to Firestore
 * 3. Query the extracted text from Firestore (with retry mechanism)
 * 4. Clean up both the image and Firestore document
 * 5. Return the extracted text
 * 
 * Features:
 * - Automatic retry mechanism (up to 3 attempts)
 * - Configurable retry delays
 * - Comprehensive error handling
 * - Automatic resource cleanup
 */

/**
 * Process an image file and extract text using Google Vision API
 * @param {File} imageFile - The image file to process
 * @param {Object} storage - Firebase Storage instance
 * @param {Object} db - Firestore instance
 * @param {number} waitTimeMs - Time to wait for processing (default: 3000ms)
 * @returns {Promise<string>} - The extracted text from the image
 * @throws {Error} - If processing fails or no text is found after retries
 */
export async function extractTextFromImage(imageFile, storage, db, waitTimeMs = 3000) {
  let uploadedImagePath = null;
  let extractedTextDocId = null;

  try {
    // Step 1: Upload image to Firebase Storage
    uploadedImagePath = await uploadImageToStorage(imageFile, storage);
    
    // Step 2: Wait for Google Vision API to process the image
    await new Promise(resolve => setTimeout(resolve, waitTimeMs));

    // Step 3: Query the extracted text from Firestore with retries
    const result = await queryExtractedTextWithRetries(uploadedImagePath, db, 5);
    
    if (!result.extractedText) {

      throw new Error('No extracted text found for this image after multiple attempts. The image may not contain readable text or processing failed.');
    }

    extractedTextDocId = result.docId;
    
    // Step 4: Clean up resources
    await cleanupResources(uploadedImagePath, extractedTextDocId, storage, db);
    
    // Step 5: Return the extracted text
    return result.extractedText;

  } catch (error) {
    // Handle extraction error
    // Clean up on error if we have resources to clean
    if (uploadedImagePath || extractedTextDocId) {
      try {
        await cleanupResources(uploadedImagePath, extractedTextDocId, storage, db);
      } catch (cleanupError) {
        // Ignore cleanup errors
      }
    }

    throw new Error('Failed to extract text from image: ' + error.message);
  }
}

/**
 * Upload image to Firebase Storage in the image_to_text/{uid} location
 * @param {File} file - The image file to upload
 * @param {Object} storage - Firebase Storage instance
 * @returns {Promise<string>} - The storage path of the uploaded image
 */
async function uploadImageToStorage(file, storage) {
  try {
    // Generate unique ID for the image
    const uid = crypto.randomUUID();
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    
    // Build storage path: image_to_text/{uid}.{extension}
    const storagePath = `image_to_text/${uid}.${fileExtension}`;
    
    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file);
    return snapshot.metadata.fullPath;
  } catch (error) {
    // Handle upload error
    throw new Error('Failed to upload image to storage: ' + error.message);
  }
}

/**
 * Query extracted text from Firestore with retry mechanism
 * @param {string} filePath - The Firebase Storage file path
 * @param {Object} db - Firestore instance
 * @param {number} maxRetries - Maximum number of retry attempts (default: 5)
 * @param {number} retryDelay - Delay between retries in milliseconds (default: 2000)
 * @returns {Promise<{extractedText: string, docId: string}>} - The extracted text and document ID
 */
async function queryExtractedTextWithRetries(filePath, db, maxRetries = 5, retryDelay = 2000) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      
      const result = await queryExtractedTextByFilePath(filePath, db);
      
      if (result.extractedText) {
        return result;
      }
      
      // If no text found and not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
      
    } catch (error) {
      // Continue to retry
      // Error logged for debugging: error
      
      // If not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }
  
  // If we get here, all attempts failed

  return { extractedText: null, docId: null };
}

/**
 * Query extracted text from Firestore by file path
 * @param {string} filePath - The Firebase Storage file path
 * @param {Object} db - Firestore instance
 * @returns {Promise<{extractedText: string, docId: string}>} - The extracted text and document ID
 */
async function queryExtractedTextByFilePath(filePath, db) {
  
  // Convert Firebase Storage path to Google Cloud Storage path
  const storageBucket = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_GS || "gs://zenokids-firebase";
  const gsPath = `${storageBucket}/${filePath}`;
  
  // Try with the passed db instance first
  let collectionRef;
  try {
    collectionRef = collection(db, 'extractedText');
  } catch (dbError) {
    // Fallback to imported db
    // Try using the imported db instance from firebase.js
    
    try {
      collectionRef = collection(firebaseDb, 'extractedText');
    } catch (importedDbError) {
      // Last resort fallback
      // Last resort: try creating a completely fresh instance
      const freshDb = getFirestore();
      
      collectionRef = collection(freshDb, 'extractedText');
    }
  }
  
  const whereClause = where('file', '==', gsPath);
  
  const q = query(collectionRef, whereClause);
  
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    return { extractedText: null, docId: null };
  }
  
  
  // Get the first matching document
  const docSnapshot = querySnapshot.docs[0];
  const data = docSnapshot.data();
  
  // Try different possible field names for extracted text
  const extractedText = data.text || data.extractedText || data.content || data.description || data.fullTextAnnotation;
  
  return {
    extractedText,
    docId: docSnapshot.id
  };
}

/**
 * Clean up both the uploaded image and the Firestore document
 * @param {string} imagePath - The Firebase Storage path of the image
 * @param {string} docId - The Firestore document ID
 * @param {Object} storage - Firebase Storage instance
 * @param {Object} db - Firestore instance
 */
async function cleanupResources(imagePath, docId, storage, db) {
  
  const cleanupPromises = [];

  // Delete the uploaded image from Firebase Storage
  if (imagePath) {
    const imageRef = ref(storage, imagePath);
    cleanupPromises.push(deleteObject(imageRef));
  }

  // Delete the extracted text document from Firestore
  if (docId) {
    const docRef = doc(db, 'extractedText', docId);
    cleanupPromises.push(deleteDoc(docRef));
  }

  // Execute both cleanup operations in parallel
  if (cleanupPromises.length > 0) {
    await Promise.all(cleanupPromises);
  }
}

/**
 * Generate sentences from extracted text by splitting on sentence boundaries
 * @param {string} extractedText - The text to split into sentences
 * @returns {Array<{languageText: string, englishText: string}>} - Array of sentence objects
 */
export function generateSentencesFromText(extractedText) {
  if (!extractedText || typeof extractedText !== 'string') {
    return [];
  }

  // Split text into sentences (basic approach)
  const textSentences = extractedText
    .split(/[.!?]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  // Generate sentences with only language text
  const sentences = textSentences.map((text) => ({
    languageText: text, // Use extracted text as language text
    englishText: '' // Leave English text empty
  }));

  return sentences;
}
